// Common properties for all field types
export interface BaseFieldConfig<T> {
  name: string; // Field name (matches model property)
  label: string;
  /** When true, modal/drawer renderers omit the visible label (still used for aria-label where applicable). */
  hideLabel?: boolean;
  description?: React.ReactNode;
  required?: boolean | ((record: any) => boolean);
  disabled?: boolean | ((record: any) => boolean);
  helpText?: string;
  group?: string; // For grouping fields in wizard steps
  width?: "full" | "half" | "third"; // For layout control
  condition?: (record: any) => boolean; // Conditional display
  validate?: (value: T) => string | undefined; // Validation function
  onChange?(value: T): void;
  value?(): T;
}

// Text input field
export interface TextField extends BaseFieldConfig<string> {
  type: "text";
  inputType?: "text" | "textarea" | "email" | "password" | "url" | "tel";
  placeholder?: string;
  /** Rows for textarea (default varies by component). */
  rows?: number;
  minLength?: number;
  maxLength?: number;
}

// Number input field
export interface NumberField extends BaseFieldConfig<number> {
  type: "number";
  placeholder?: string;
  /** Optional prefix to display before the number */
  prefix?: string;
  min?: number;
  max?: number;
  step?: number;
}

// Dollars input: stores cents in record[name], displays and edits as dollars (e.g. 25.50)
export interface DollarsField extends BaseFieldConfig<number> {
  type: "dollars";
  placeholder?: string;
  min?: number;
  max?: number;
  step?: number;
}

// Select/Radio field
export interface SelectField<T extends string | number> extends BaseFieldConfig<T> {
  type: "select";
  options: FormFieldOption<T>[];
  /** Optional override to force a specific variant */
  forceVariant?: "select" | "radio-vertical" | "radio-horizontal";
  /** When true, the empty "Select..." option is omitted (use when every option is valid). */
  hidePlaceholderOption?: boolean;
}
export function selectField<T extends string | number>(config: Omit<SelectField<T>, "type">): SelectField<T> {
  return {
    ...config,
    type: "select",
  };
}

// Checkbox field
export interface CheckboxField extends BaseFieldConfig<boolean> {
  type: "checkbox";
  text?: string; // Text to display next to checkbox
  /** Button variant: labels and kinds for checked/unchecked states */
  buttonVariant?: {
    checkedLabel: string; // Label when checked (e.g., "Retire")
    uncheckedLabel: string; // Label when unchecked (e.g., "Join session")
    checkedKind?:
      | "primary"
      | "secondary"
      | "accent"
      | "plain"
      | "link"
      | "danger"
      | "action"
      | "dropdown"
      | "select"; // Button kind when checked
    uncheckedKind?:
      | "primary"
      | "secondary"
      | "accent"
      | "plain"
      | "link"
      | "danger"
      | "action"
      | "dropdown"
      | "select"; // Button kind when unchecked
  };
}

// Date/time field
export interface DatetimeField extends BaseFieldConfig<Date> {
  type: "datetime";
  includeTime?: boolean;
}

// Time-only field (stores as string like "18:00")
export interface TimeField extends BaseFieldConfig<string> {
  type: "time";
}

// Custom component field
export interface CustomField extends BaseFieldConfig<any> {
  type: "custom";
  component: React.ComponentType<CustomComponentFieldProps>;
  /** Extra classes on the ModalFormRenderer wrapper (e.g. `hideLabel` outer div). */
  wrapperClassName?: string;
}

export interface HtmlField extends BaseFieldConfig<string> {
  type: "html";
  /** Optional className for the Editor (e.g. min-h for taller editing area). */
  className?: string;
  /** Optional merge-variable dropdown shown in the editor toolbar. */
  insertableVariables?: Array<{ label: string; token: string }>;
  /** Sanitizer passed to `Editor` (should match persist/display rules). */
  sanitizeHtml?: (html: string) => string;
}

export interface UploadField extends BaseFieldConfig<string> {
  type: "upload";
  variant: "image" | "pdf";
}

// Segmented input field for formatted numeric values
export interface SegmentedField extends BaseFieldConfig<string> {
  type: "segmented";
  format: string; // Format pattern like "XXX-XXXX-XXXX"
  showValid?: boolean; // Whether to show a checkmark when valid
  prefix?: React.ReactNode; // Optional prefix to display
  suffix?: string; // Optional suffix to display
}

// Fieldset for grouping fields
export interface FieldsetField extends BaseFieldConfig<any> {
  type: "fieldset";
  fields: FormFieldConfig[];
}

// Union type of all field types
export type FormFieldConfig =
  | TextField
  | NumberField
  | DollarsField
  | SelectField<any>
  | CheckboxField
  | DatetimeField
  | TimeField
  | CustomField
  | HtmlField
  | UploadField
  | SegmentedField
  | FieldsetField;

// Option type for select and radio fields
export interface FormFieldOption<T extends string | number> {
  label: string;
  description?: React.ReactNode;
  value: T;
  icon?: React.ReactNode;
}

export interface CustomComponentFieldProps<R = any> {
  field: CustomField;
  record: R;
  value: any;
  onChange(value: any): void;
}

type TypeToFieldConfig<T extends FormFieldConfig["type"]> = Extract<FormFieldConfig, { type: T }>;

export function field<T extends FormFieldConfig["type"], F extends TypeToFieldConfig<T>>(
  name: string,
  type: T,
  config: Omit<F, "name" | "type"> & Partial<Pick<F, "name" | "type">>,
): F {
  return {
    ...config,
    name,
    type,
  } as F;
}
